import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import Modal from '@components/common/modal';
import useJobGroupsWithTitlesDesc, {
  useJobGroupJobTitlesForUser,
  useSaveJobGroupTitles,
} from '@components/match/query/useJobGroupsWithTitles';
import { ProfileInfoType } from '@components/match/profile/types';
import DeveloperJobSelectModal from './developerJobSelectModal';
import JobSelectModal from './jobSelectModal';
import IJobGroupWithTitlesVO, { IJobGroupWithTitlesFormVO } from '@domain/vo/match/IJobGroupWithTitlesVO';

import { JobGroupProgrammerCodeList, JobGroupGeneralCodeList } from '@domain/constant/JobGroupJobTitleType';
import { RouteUrl } from '@utils/routeUtil';

const jobGroupFieldName = 'jobGroupCode';

interface IJobGroupTitlesSelectModalProps {
  visible: boolean;
  onSuccess: () => void;
  onClose: () => void;
  profileInfoType?: ProfileInfoType;
}

const JobGroupTitlesSelectModal = ({
  visible,
  onSuccess,
  onClose,
  profileInfoType,
}: IJobGroupTitlesSelectModalProps) => {
  const { push } = useRouter();

  const methods = useForm<IJobGroupWithTitlesFormVO>({
    mode: 'onChange',
  });

  const [redirectUrl, setRedirectUrl] = useState('');

  const { refetch: refetchSavedJobGroup } = useJobGroupJobTitlesForUser(false);
  const { data, isFetched } = useJobGroupsWithTitlesDesc();
  const { mutate: saveJobGroupTitlesMutate } = useSaveJobGroupTitles(() => {
    onSuccess();
    if (redirectUrl) {
      setRedirectUrl('');
      push(redirectUrl);
      return;
    }
  });

  if (!data || !isFetched) return null;

  const viewData = data.filter((item) => {
    switch (profileInfoType) {
      case 'GENERAL':
        return JobGroupGeneralCodeList.includes(item.code);
      case 'DEVELOPER':
        return JobGroupProgrammerCodeList.includes(item.code);
      default:
        return true;
    }
  });

  const onSubmit = async () => {
    const formValue = methods.getValues();
    if (!formValue) return;
    const { data } = await refetchSavedJobGroup();

    if (data) {
      // 기존에 저장되어 있는 직군 직무 있으면 같이 저장되도록 보냄
      data.forEach((item) => formValue.jobGroup.push(item as IJobGroupWithTitlesVO));
    }

    await saveJobGroupTitlesMutate(formValue.jobGroup);

    if (!profileInfoType) {
      setRedirectUrl(RouteUrl.MATCH);
    }
  };

  return (
    <Modal visible={visible}>
      <FormProvider {...methods}>
        {profileInfoType === 'DEVELOPER' ? (
          <DeveloperJobSelectModal
            onClose={onClose}
            jobGroupFieldName={jobGroupFieldName}
            onSubmit={onSubmit}
            viewData={viewData}
          />
        ) : (
          <JobSelectModal
            onClose={onClose}
            jobGroupFieldName={jobGroupFieldName}
            onSubmit={onSubmit}
            viewData={viewData}
          />
        )}
      </FormProvider>
    </Modal>
  );
};

export default JobGroupTitlesSelectModal;
